import React from "react";
import styled from "styled-components";
import Page from "../../components/Page";
import { Button, ColoredButton } from "../../components/buttons";
import api from "../../services/api";
import toasts from "../../services/toasts";

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const Card = styled.div`
  background: var(--button-bg);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  box-shadow: var(--button-shadow);
  padding: 18px;
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 14px;

  @media (max-width: 800px) {
    flex-direction: column;
  }
`;

const Title = styled.h4`
  margin: 0 0 5px;
`;

const Note = styled.p`
  margin: 0;
  opacity: 0.76;
  line-height: 1.5;
`;

const Status = styled.span`
  display: inline-flex;
  border-radius: 999px;
  padding: 5px 9px;
  font-size: 12px;
  font-weight: 700;
  background: ${({ active }) =>
    active ? "rgba(48, 164, 108, 0.16)" : "rgba(160, 160, 160, 0.16)"};
`;

const VenueList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const VenueCard = styled.div`
  border: 1px solid var(--border-color);
  border-radius: 10px;
  padding: 14px;
  background: var(--app-bg);
`;

const VenueHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
`;

const VenueTitle = styled.div`
  font-weight: 700;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(220px, 1fr));
  gap: 12px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const FullWidth = styled.div`
  grid-column: 1 / -1;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 6px;
  font-weight: 600;
  font-size: 13px;
`;

const Input = styled.input`
  box-sizing: border-box;
  width: 100%;
  padding: 9px 10px;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  background: var(--button-bg);
  color: var(--text-color);
`;

const Select = styled.select`
  box-sizing: border-box;
  width: 100%;
  padding: 9px 10px;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  background: var(--button-bg);
  color: var(--text-color);
`;

const CheckboxRow = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 14px;
`;

const AddCard = styled(VenueCard)`
  border-style: dashed;
  margin-top: 14px;
`;

const EmptyState = styled.div`
  padding: 30px 0;
  opacity: 0.75;
`;

const getErrorMessage = error => {
  if (error && error.response && error.response.data) {
    return error.response.data.message || error.response.data.error || error.toString();
  }
  return error.toString();
};

const defaultNewVenue = source => ({
  source,
  slug: "",
  site_id: "",
  venue_name: "",
  url: "",
  city: "London",
  country_code: "gb",
  timezone: "Europe/London",
  place_id: "",
  is_active: true,
  notes: ""
});

class WhatsOnSources extends React.Component {
  state = {
    loading: true,
    payload: null,
    drafts: {},
    addingSource: null,
    newVenue: null,
    savingKey: null
  };

  componentDidMount() {
    this.load();
  }

  load = async () => {
    this.setState({ loading: true });
    try {
      const payload = await api.whats_on_sources.list();
      this.setState({ payload, loading: false, drafts: {} });
    } catch (error) {
      this.setState({ loading: false });
      toasts.error(getErrorMessage(error));
    }
  };

  venueKey = venue => `${venue.source}:${venue.mapping_id || venue.slug}`;

  venueDraft = venue => {
    const key = this.venueKey(venue);
    if (this.state.drafts[key]) {
      return this.state.drafts[key];
    }
    return {
      ...venue,
      place_id: venue.place_id ? String(venue.place_id) : "",
      is_active: venue.mapping_id ? !!venue.is_active : true
    };
  };

  updateVenue = (venue, field, value) => {
    const key = this.venueKey(venue);
    this.setState(state => ({
      drafts: {
        ...state.drafts,
        [key]: {
          ...this.venueDraft(venue),
          [field]: value
        }
      }
    }));
  };

  updateNewVenue = (field, value) => {
    this.setState(state => ({
      newVenue: {
        ...state.newVenue,
        [field]: value
      }
    }));
  };

  saveVenue = async (venue, isNew = false) => {
    const draft = isNew ? this.state.newVenue : this.venueDraft(venue);
    const key = isNew ? `new:${draft.source}` : this.venueKey(venue);
    if (!draft.place_id) {
      toasts.error("Choose the Playground place for this venue.");
      return;
    }

    this.setState({ savingKey: key });
    try {
      const saved = await api.whats_on_sources.saveVenue({
        ...draft,
        mapping_id: draft.mapping_id || undefined,
        place_id: Number(draft.place_id)
      });
      toasts.success(
        `${saved.venue_name} is configured and mapped to ${saved.place_name}.`
      );
      this.setState({ addingSource: null, newVenue: null });
      await this.load();
    } catch (error) {
      toasts.error(getErrorMessage(error));
    } finally {
      this.setState({ savingKey: null });
    }
  };

  startAdding = source => {
    this.setState({
      addingSource: source,
      newVenue: defaultNewVenue(source)
    });
  };

  cancelAdding = () => {
    this.setState({ addingSource: null, newVenue: null });
  };

  renderFields = (source, venue, onChange, candidates) => {
    const mappedPlaceIds = new Set(
      (source.venues || [])
        .filter(item => item.mapping_id && item.mapping_id !== venue.mapping_id)
        .map(item => String(item.place_id))
    );

    return (
      <Grid>
        <div>
          <Label>Venue name</Label>
          <Input
            value={venue.venue_name || ""}
            onChange={event => onChange("venue_name", event.target.value)}
            placeholder="Curzon Richmond"
          />
        </div>
        <div>
          <Label>External slug</Label>
          <Input
            value={venue.slug || ""}
            onChange={event => onChange("slug", event.target.value)}
            placeholder="richmond"
          />
        </div>
        <div>
          <Label>External site or cinema ID</Label>
          <Input
            value={venue.site_id || ""}
            onChange={event => onChange("site_id", event.target.value)}
            placeholder="Source-specific identifier"
          />
        </div>
        <div>
          <Label>City</Label>
          <Input
            value={venue.city || ""}
            onChange={event => onChange("city", event.target.value)}
            placeholder="London"
          />
        </div>
        <FullWidth>
          <Label>Listings or venue URL</Label>
          <Input
            value={venue.url || ""}
            onChange={event => onChange("url", event.target.value)}
            placeholder="https://..."
          />
        </FullWidth>
        <div>
          <Label>Timezone</Label>
          <Input
            value={venue.timezone || ""}
            onChange={event => onChange("timezone", event.target.value)}
            placeholder="Europe/London"
          />
        </div>
        <div>
          <Label>Country code</Label>
          <Input
            value={venue.country_code || ""}
            onChange={event => onChange("country_code", event.target.value)}
            placeholder="gb"
          />
        </div>
        <FullWidth>
          <Label>Mapped Playground place</Label>
          <Select
            value={venue.place_id || ""}
            onChange={event => onChange("place_id", event.target.value)}
          >
            <option value="">Choose a place</option>
            {candidates.map(candidate => {
              const placeId = String(candidate.place_id);
              const unavailable =
                mappedPlaceIds.has(placeId) && placeId !== String(venue.place_id || "");
              return (
                <option
                  key={`${source.source}:${candidate.place_id}`}
                  value={candidate.place_id}
                  disabled={unavailable}
                >
                  {candidate.place_name} (#{candidate.place_id})
                  {candidate.place_address ? ` — ${candidate.place_address}` : ""}
                  {unavailable ? " — already used by this source" : ""}
                </option>
              );
            })}
          </Select>
        </FullWidth>
        <FullWidth>
          <CheckboxRow>
            <input
              type="checkbox"
              checked={!!venue.is_active}
              onChange={event => onChange("is_active", event.target.checked)}
            />
            Include this venue in the next scheduled scrape
          </CheckboxRow>
        </FullWidth>
      </Grid>
    );
  };

  renderSource = (source, candidates) => {
    const adding = this.state.addingSource === source.source;
    return (
      <Card key={source.source}>
        <HeaderRow>
          <div>
            <Title>{source.label}</Title>
            <Note>
              {source.active_venue_count} active of {source.venue_count} configured.{" "}
              {source.configuration_note}
            </Note>
          </div>
          {source.allows_additions ? (
            <ColoredButton
              type="button"
              onClick={() => this.startAdding(source.source)}
              disabled={adding}
            >
              Add {source.label} venue
            </ColoredButton>
          ) : (
            <Status active={false}>Adapter required for another website</Status>
          )}
        </HeaderRow>

        <VenueList>
          {(source.venues || []).map(venue => {
            const draft = this.venueDraft(venue);
            const key = this.venueKey(venue);
            return (
              <VenueCard key={key}>
                <VenueHeader>
                  <div>
                    <VenueTitle>{venue.venue_name}</VenueTitle>
                    <Note>
                      {venue.mapping_id
                        ? `Mapped to ${venue.place_name} (#${venue.place_id})`
                        : "Default venue — choose a place to activate it"}
                    </Note>
                  </div>
                  <Status active={!!venue.is_active}>
                    {venue.is_active ? "Active" : "Inactive"}
                  </Status>
                </VenueHeader>
                {this.renderFields(
                  source,
                  draft,
                  (field, value) => this.updateVenue(venue, field, value),
                  candidates
                )}
                <Actions>
                  <Button
                    type="button"
                    onClick={() => this.saveVenue(venue)}
                    disabled={this.state.savingKey === key}
                  >
                    {this.state.savingKey === key ? "Saving..." : "Save configuration"}
                  </Button>
                </Actions>
              </VenueCard>
            );
          })}
        </VenueList>

        {adding && this.state.newVenue ? (
          <AddCard>
            <VenueHeader>
              <div>
                <VenueTitle>New {source.label} venue</VenueTitle>
                <Note>
                  This will use the existing {source.label} scraper adapter on the next run.
                </Note>
              </div>
            </VenueHeader>
            {this.renderFields(
              source,
              this.state.newVenue,
              this.updateNewVenue,
              candidates
            )}
            <Actions>
              <Button type="button" onClick={this.cancelAdding}>
                Cancel
              </Button>
              <ColoredButton
                type="button"
                onClick={() => this.saveVenue(null, true)}
                disabled={this.state.savingKey === `new:${source.source}`}
              >
                {this.state.savingKey === `new:${source.source}`
                  ? "Saving..."
                  : "Add venue"}
              </ColoredButton>
            </Actions>
          </AddCard>
        ) : null}
      </Card>
    );
  };

  render() {
    const { loading, payload } = this.state;
    return (
      <Page title="What's On Sources">
        <Stack>
          <Card>
            <Title>Scheduled scraper configuration</Title>
            <Note>
              Configure the venues used by the existing scraper adapters. Changes are read
              directly from the database when the weekly AWS task starts; they do not trigger a
              scrape immediately. Entirely new websites still need a parser adapter.
            </Note>
            {payload && payload.safety_note ? <Note>{payload.safety_note}</Note> : null}
          </Card>

          {loading ? <Card>Loading What's On sources...</Card> : null}
          {!loading && payload
            ? (payload.sources || []).map(source =>
                this.renderSource(source, payload.place_candidates || [])
              )
            : null}
          {!loading && !payload ? (
            <Card>
              <EmptyState>What's On source configuration could not be loaded.</EmptyState>
            </Card>
          ) : null}
        </Stack>
      </Page>
    );
  }
}

export default WhatsOnSources;
